import {
  ColumnImportSchema,
  DocSchema,
  DocSchemaImportWarning,
  DocTableSchema,
  ImportSchema,
} from "app/common/DocSchemaImport";
import { extractTypeFromColType, getReferencedTableId, isRealFormulaColumn } from "app/common/gristTypes";
import { ColumnMetadata } from "app/plugin/DocApiTypes";
import { GristType } from "app/plugin/GristData";

/**
 * Converts the schema of a source Grist document into an {@link ImportSchema} for the
 * destination document.
 *
 * Grist-to-Grist schema mapping mostly produces an identical schema, with a few notable
 * differences:
 * - Formula columns keep their formula, copied across as-is. Table and column ids named in it
 *   are not rewritten, so it only resolves if the destination ends up with the same ids
 * - Trigger formulas are stripped: those columns become plain data columns holding their
 *   last computed value
 * - Attachment columns are not imported at all, and produce a warning
 * - Ref/RefList columns become internal schema references, resolved to destination tables
 *   during import
 *
 * Every table also gains a leading {@link SourceRowIdColumnLabel} column, holding each record's
 * row id in the source document, so that importing the same source again updates the rows it
 * created rather than duplicating them.
 */
export function gristImportSchemaFromSourceSchema(
  sourceSchema: DocSchema,
): { schema: ImportSchema; warnings: DocSchemaImportWarning[] } {
  const warnings: DocSchemaImportWarning[] = [];

  const schema: ImportSchema = {
    tables: sourceSchema.tables.map((table) => {
      const columns: ColumnImportSchema[] = [];
      for (const column of table.columns) {
        const result = convertSourceColumnToColumnSchema(table, column);
        if (result.warning) { warnings.push(result.warning); }
        if (result.column) { columns.push(result.column); }
      }
      return {
        originalId: table.id,
        desiredGristId: table.id,
        columns: [createSourceRowIdColumnSchema(), ...columns],
      };
    }),
  };

  return { schema, warnings };
}

/** Label of the column holding each imported record's row id in the source document. */
export const SourceRowIdColumnLabel = "Source Row Id";

/**
 * Builds the schema of the source row id column, which every imported table gets.
 *
 * Mirrors createAirtableIdColumnSchema from AirtableSchemaImporter.ts, but holds an Int rather
 * than text, so that it sorts and filters as the number it is.
 */
function createSourceRowIdColumnSchema(): ColumnImportSchema {
  return {
    originalId: "sourceRowId",
    desiredGristId: "Source_Row_Id",
    type: "Int",
    label: SourceRowIdColumnLabel,
    untieColIdFromLabel: true,
  };
}

function convertSourceColumnToColumnSchema(
  table: DocTableSchema, columnMetadata: ColumnMetadata,
): { column?: ColumnImportSchema, warning?: DocSchemaImportWarning } {
  const { label, type, description, widgetOptions } = columnMetadata.fields;

  if (type === "Attachments") {
    return { warning: new AttachmentColumnWarning(table, columnMetadata) };
  }

  const column: ColumnImportSchema = {
    originalId: columnMetadata.id,
    desiredGristId: columnMetadata.id,
    label: label !== undefined ? String(label) : undefined,
    type: type as GristType,
  };
  if (typeof description === "string" && description !== "") {
    column.description = description;
  }
  const parsedWidgetOptions = parseWidgetOptions(widgetOptions);
  if (parsedWidgetOptions) {
    column.widgetOptions = parsedWidgetOptions;
  }

  if (isRealFormulaColumn(columnMetadata.fields)) {
    column.isFormula = true;
    column.formula = { formula: String(columnMetadata.fields.formula) };
  }

  const referencedTableId = getReferencedTableId(type);
  if (referencedTableId) {
    column.type = extractTypeFromColType(type) as GristType;
    column.ref = { originalTableId: referencedTableId };
  }

  return { column };
}

class AttachmentColumnWarning implements DocSchemaImportWarning {
  public readonly message: string;
  public readonly ref: { originalTableId: string, originalColId: string };

  constructor(table: DocTableSchema, column: ColumnMetadata) {
    const columnName = column.fields.label ?? column.id;
    this.message = `Column '${columnName}' of table '${table.id}' holds attachments, ` +
      "which cannot be copied, and will be skipped.";
    this.ref = { originalTableId: table.id, originalColId: column.id };
  }
}

function parseWidgetOptions(widgetOptions: unknown): Record<string, any> | undefined {
  if (typeof widgetOptions !== "string" || widgetOptions === "") { return undefined; }
  try {
    const parsed = JSON.parse(widgetOptions);
    return typeof parsed === "object" && parsed !== null ? parsed : undefined;
  } catch (e) {
    return undefined;
  }
}
